import { html } from "lit-html";
import i18next from "i18next";
import dayjs from "dayjs";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import { useNewFlightDatePicker } from "./useNewFlightDatePicker";
import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { useTimetable } from "./useTimetable";
import { useReduxState } from "Shared/redux/useReduxState";
import { COMMON_DAYJS_FORMAT } from "Services/constants";
import { DynamicSettings } from "Components/admin/DynamicSettings";
dayjs.extend(CustomParseFormat);

export interface Props {
	culture: string;
	dynamicSettings: DynamicSettings;
	openPassengerSelector: () => void;
	startLoading: () => LoaderInstance;
	stopLoading: (loader: LoaderInstance) => void;
}

export const useNewFlightDateSelector = (props: Props) => {
	const [currentSearch, setCurrentSearch] = useReduxState("currentSearch");

	const timetable = useTimetable({ startLoading: props.startLoading, stopLoading: props.stopLoading });

	const timetableReady = currentSearch.originCity && currentSearch.destinationCity && !timetable.isLoading;

	const autoOpenDeparture = () => {
		if (timetableReady && !currentSearch.departureDate) departureDate.open();
	};

	const departureDate = useNewFlightDatePicker({
		availableSchedules: timetable.outboundAvailableDates,
		culture: props.culture,
		disabledDates: timetable.disabledDepartureDates,
		dynamicSettings: props.dynamicSettings,
		inputTestId: "",
		isDisabled: !timetableReady,
		isLoading: timetable.isLoading,
		isReturnDate: false,
		max: timetable.maxDepartureDate,
		min: timetable.minDepartureDate,
		placeHolder: i18next.t("Fecha Ida"),
		rangeStart: undefined,
		value: currentSearch.departureDate,
		onChange: (date) => {
			setCurrentSearch({ ...currentSearch, departureDate: date });
            if (currentSearch.isOneWay) return;
			returnDate.open();
		},
	});

	const returnDate = useNewFlightDatePicker({
		availableSchedules: timetable.inboundAvailableDates,
		culture: props.culture,
		disabledDates: timetable.disabledReturnDates,
		dynamicSettings: props.dynamicSettings,
		inputTestId: "",
		isDisabled: !timetableReady || !currentSearch.departureDate || currentSearch.isOneWay,
		isLoading: timetable.isLoading,
		isReturnDate: true,
		max: timetable.maxReturnDate,
		min: timetable.minReturnDate,
		placeHolder: i18next.t("Fecha Vuelta"),
		rangeStart: currentSearch.departureDate,
		value: currentSearch.returnDate,
		onChange: (date) => {
			setCurrentSearch({ ...currentSearch, returnDate: date });
			props.openPassengerSelector();
		},
	});

	useEffectWithoutCleanup(autoOpenDeparture, [
		timetable.isLoading,
		currentSearch.departureDate?.format(COMMON_DAYJS_FORMAT),
		currentSearch.originCity?.code,
		currentSearch.destinationCity?.code,
	]);

	const htmlTemplate = () => html`
		<div>${departureDate.htmlTemplate()}</div>
		<div>${returnDate.htmlTemplate()}</div>
	`;

	return {
		selectedPriceOutbound: timetable.selectedPriceOutbound,
		selectedPriceInbound: timetable.selectedPriceInbound,
		htmlTemplate,
		setMinReturnDate: timetable.setMinReturnDate,
	};
};
